import React, {useCallback, useEffect, useRef, useState} from 'react';
import createAxiosInstance from "../support/AxiosSupport";
import usePasteImages, {IMAGE_ACCEPT, splitAcceptedImages, uniqueFileId} from "../hooks/usePasteImages";
import AuthImage from "./AuthImage";
import Dialog from "./Dialog";
import {CameraIcon, PlusIcon, XIcon} from "./icons";

interface ImageSlot {
    id: string;
    key?: string;
    file?: File;
    previewUrl: string;
    uploading?: boolean;
    error?: string;
}

interface AddAiRecognitionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const AddAiRecognitionModal: React.FC<AddAiRecognitionModalProps> = ({
    isOpen,
    onClose,
    onSuccess
}) => {
    const [description, setDescription] = useState('');
    const [images, setImages] = useState<ImageSlot[]>([]);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [cameraOpen, setCameraOpen] = useState(false);
    const [cameraError, setCameraError] = useState<string | null>(null);
    const videoRef = useRef<HTMLVideoElement>(null);
    const streamRef = useRef<MediaStream | null>(null);

    /** Текст ошибки загрузки: описание от сервера, иначе сообщение клиента. */
    const uploadErrorText = (err: unknown): string => {
        const e = err as Error & {response?: {data?: {description?: string; message?: string}}};
        return e?.response?.data?.description || e?.response?.data?.message || e?.message || 'Не удалось загрузить';
    };

    /**
     * Единый путь для всех источников фото — выбор файла, камера, буфер обмена.
     * Формат и размер проверяем до отправки: сервер отверг бы их так же, но
     * пользователю нужен ответ сразу и словами, а не после загрузки.
     */
    const uploadImages = useCallback(async (files: File[]) => {
        const {accepted, rejected} = splitAcceptedImages(files);
        if (rejected.length > 0) {
            setError(rejected.length === 1
                ? `${rejected[0].file.name}: ${rejected[0].reason}`
                : `Пропущено файлов: ${rejected.length}. ${rejected[0].reason}`);
        }
        if (accepted.length === 0) return;

        const api = await createAxiosInstance();
        const newSlots: ImageSlot[] = accepted.map(file => ({
            id: `img-${uniqueFileId()}`,
            file,
            previewUrl: URL.createObjectURL(file),
            uploading: true
        }));

        setImages(prev => [...prev, ...newSlots]);

        for (const slot of newSlots) {
            const formData = new FormData();
            formData.append('file', slot.file!);
            try {
                const res = await api.post<{ key: string }>(
                    '/clothing/images',
                    formData,
                    {headers: {'Content-Type': 'multipart/form-data'}}
                );
                setImages(prev => prev.map(s =>
                    s.id === slot.id ? {...s, key: res.data.key, uploading: false} : s
                ));
            } catch (err: unknown) {
                setImages(prev => prev.map(s => {
                    if (s.id === slot.id) {
                        URL.revokeObjectURL(s.previewUrl);
                        return {...s, error: uploadErrorText(err), uploading: false};
                    }
                    return s;
                }));
            }
        }
    }, []);

    // Вставка из буфера (paste-photo-from-clipboard, FR-1): скриншот или скопированная
    // картинка идут тем же путём, что выбранный файл и снимок камерой.
    usePasteImages(uploadImages, isOpen);

    const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        e.target.value = '';
        if (files.length > 0) uploadImages(files);
    }, [uploadImages]);

    useEffect(() => {
        if (!isOpen) setCameraOpen(false);
    }, [isOpen]);

    useEffect(() => {
        if (!cameraOpen || !isOpen) return;
        setCameraError(null);
        navigator.mediaDevices.getUserMedia({video: {facingMode: 'environment'}})
            .then((stream) => {
                streamRef.current = stream;
                if (videoRef.current) videoRef.current.srcObject = stream;
            })
            .catch((err) => {
                setCameraError(err.name === 'NotAllowedError' ? 'Доступ к камере запрещён' : err.message || 'Ошибка камеры');
            });
        return () => {
            streamRef.current?.getTracks().forEach(t => t.stop());
            streamRef.current = null;
        };
    }, [cameraOpen, isOpen]);

    const capturePhoto = useCallback(() => {
        const video = videoRef.current;
        if (!video || !video.srcObject || video.readyState !== 4) return;
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        ctx.drawImage(video, 0, 0);
        canvas.toBlob((blob) => {
            if (!blob) return;
            const file = new File([blob], `photo-${uniqueFileId()}.jpg`, {type: 'image/jpeg'});
            uploadImages([file]);
            setCameraOpen(false);
        }, 'image/jpeg', 0.9);
    }, [uploadImages]);

    const removeImage = useCallback((index: number) => {
        setImages(prev => {
            const slot = prev[index];
            if (slot.previewUrl) URL.revokeObjectURL(slot.previewUrl);
            return prev.filter((_, i) => i !== index);
        });
    }, []);

    const handleSubmit = useCallback(async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        const photoKeys = images
            .filter(s => s.key && !s.error)
            .map(s => s.key!);
        const hasUploading = images.some(s => s.uploading);
        if (hasUploading) {
            setError('Дождитесь загрузки всех изображений');
            return;
        }
        if (photoKeys.length === 0) {
            setError('Загрузите хотя бы одно фото');
            return;
        }

        setSaving(true);
        try {
            const api = await createAxiosInstance();
            await api.post('/ai-recognition', {
                photoKeys,
                description: description.trim() || undefined
            });
            onSuccess();
            onClose();
            setDescription('');
            setImages(prev => {
                prev.forEach(s => s.previewUrl && URL.revokeObjectURL(s.previewUrl));
                return [];
            });
        } catch (err: unknown) {
            setError(uploadErrorText(err));
        } finally {
            setSaving(false);
        }
    }, [description, images, onSuccess, onClose]);

    const handleClose = useCallback(() => {
        setCameraOpen(false);
        images.forEach(s => s.previewUrl && URL.revokeObjectURL(s.previewUrl));
        setImages([]);
        setDescription('');
        onClose();
    }, [images, onClose]);

    return (
        // Пока открыт слой камеры, Escape и клик по фону адресованы ему, а не форме.
        <Dialog
            open={isOpen}
            onClose={handleClose}
            dismissible={!cameraOpen}
            title="Распознать вещь по фото (AI)"
            data-testid="add-ai-recognition-modal"
            overlayClassName="fw-overlay fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
            panelClassName="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto outline-none"
            bodyClassName=""
        >
                <form onSubmit={handleSubmit} className="p-4 space-y-4">
                    {error && (
                        <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">
                            {error}
                        </div>
                    )}
                    <div>
                        <div className="flex items-baseline justify-between gap-2 mb-1">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                                Фотографии *
                            </label>
                            <span className="hidden sm:inline text-xs text-gray-500 dark:text-gray-400">
                                можно вставить из буфера: Ctrl/⌘+V
                            </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {images.map((slot, idx) => (
                                <div key={slot.id} className="relative w-20 h-20 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-600">
                                    {slot.key && !slot.error ? (
                                        <AuthImage imageKey={slot.key} alt="" variant="thumbnail" className="w-full h-full object-cover"/>
                                    ) : (
                                        <div className="w-full h-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                                            {slot.uploading ? (
                                                <span className="text-xs text-gray-500">Загрузка...</span>
                                            ) : slot.error ? (
                                                <span className="text-xs text-red-500 text-center px-1">{slot.error}</span>
                                            ) : (
                                                <img src={slot.previewUrl} alt="" className="w-full h-full object-cover"/>
                                            )}
                                        </div>
                                    )}
                                    {/* Кнопка видна всегда: на тач-экранах hover нет, а прятать её —
                                        значит лишить пользователя телефона возможности убрать фото. */}
                                    <button
                                        type="button"
                                        onClick={() => removeImage(idx)}
                                        aria-label="Убрать фото"
                                        className="absolute top-0.5 right-0.5 w-7 h-7 flex items-center justify-center rounded-md
                                                   bg-black/60 text-white hover:bg-black/80 transition-colors duration-fast"
                                    >
                                        <XIcon className="w-4 h-4"/>
                                    </button>
                                </div>
                            ))}
                            <label className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 flex items-center justify-center cursor-pointer hover:border-primary-500 dark:hover:border-primary-400 transition-colors" title="Выбрать из галереи">
                                <PlusIcon className="w-8 h-8 text-gray-400" aria-hidden="true"/>
                                <span className="sr-only">Выбрать из галереи</span>
                                <input type="file" accept={IMAGE_ACCEPT} multiple className="hidden" onChange={handleFileSelect}/>
                            </label>
                            <button
                                type="button"
                                onClick={() => setCameraOpen(true)}
                                className="w-20 h-20 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 flex flex-col items-center justify-center gap-1 hover:border-primary-500 dark:hover:border-primary-400 transition-colors"
                                title="Сделать фото"
                            >
                                <CameraIcon className="w-8 h-8 text-gray-400" aria-hidden="true"/>
                                <span className="text-xs text-gray-500 dark:text-gray-400">Камера</span>
                            </button>
                        </div>
                    </div>
                    {cameraOpen && (
                        <Dialog
                            open
                            onClose={() => setCameraOpen(false)}
                            ariaLabel="Камера"
                            data-testid="camera-layer"
                            overlayClassName="fw-overlay fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4"
                            panelClassName="w-full max-w-lg outline-none"
                            bodyClassName=""
                        >
                                {cameraError ? (
                                    <div className="bg-white dark:bg-gray-800 rounded-lg p-6 text-center">
                                        <p className="text-red-600 dark:text-red-400 mb-4">{cameraError}</p>
                                        <button onClick={() => setCameraOpen(false)} className="btn-secondary">Закрыть</button>
                                    </div>
                                ) : (
                                    <>
                                        <video
                                            ref={videoRef}
                                            autoPlay
                                            playsInline
                                            muted
                                            className="w-full rounded-lg bg-black aspect-[4/3] object-cover"
                                        />
                                        <div className="flex gap-2 mt-4 justify-center">
                                            <button
                                                type="button"
                                                onClick={() => setCameraOpen(false)}
                                                className="btn-secondary"
                                            >
                                                Отмена
                                            </button>
                                            <button
                                                type="button"
                                                onClick={capturePhoto}
                                                className="btn-primary flex items-center gap-2"
                                            >
                                                <CameraIcon className="w-5 h-5"/>
                                                Сделать фото
                                            </button>
                                        </div>
                                    </>
                                )}
                        </Dialog>
                    )}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Описание (по желанию)
                        </label>
                        <textarea
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            rows={3}
                            className="input-field w-full"
                            placeholder="Например: зимняя куртка, куплена в 2023"
                        />
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                        <button type="button" onClick={handleClose} className="btn-secondary">
                            Отмена
                        </button>
                        <button type="submit" disabled={saving} className="btn-primary">
                            {saving ? 'Отправка...' : 'Отправить на распознавание'}
                        </button>
                    </div>
                </form>
        </Dialog>
    );
};

export default AddAiRecognitionModal;
